// SiteFooter — THE footer, for every React surface.
//
// The markup is not written here. It is read from src/shared/site-footer.html,
// the same file scripts/apply-site-footer.cjs injects into all 1,500+ static
// pages in public/. One file, two renderers: the app and the static site
// cannot drift apart, which is exactly how the site ended up with 36 different
// footers before this.
//
// Styles come from public/site-footer.css, linked once in the page shells
// (index.html and spa.html) — the same stylesheet the static pages link.
import { useEffect, useRef } from "react";
import rawFooter from "@/shared/site-footer.html?raw";

// The partial carries a maintainer comment and the <footer> wrapper. Strip
// both: the wrapper is re-created as a real element below so the rendered
// markup matches the static pages tag for tag, with no extra container.
const INNER = rawFooter
  .replace(/^<!--[\s\S]*?-->\s*/, "")
  .replace(/^\s*<footer[^>]*>/i, "")
  .replace(/<\/footer>\s*$/i, "")
  .replace("{{MF_TRUST_COUNTS}}", "")
  .trim();

// T1251: the counts beside the trust line, from this build's public/trust-counts.json (scripts/build-trust-counts.cjs,
// the same numbers the static pages carry). Missing or not ok: the line shows no counts. Empty beats wrong.
type TrustCounts = { ok?: boolean; year?: number; records_shown?: number; filings_shown?: number };
const fmt = (n: number) => Number(n).toLocaleString("en-US");
function fillCounts(el: HTMLElement | null, c: TrustCounts) {
  const box = el?.querySelector(".site-footer__counts");
  if (!box || !c?.ok || !(Number(c.records_shown) > 0) || !(Number(c.filings_shown) > 0)) return;
  const a = document.createElement("span");
  a.className = "site-footer__count";
  a.title = "Company record pages MentionFox publishes for free, each fact linked to the filing it comes from";
  a.textContent = `${fmt(c.records_shown as number)}+ free company record pages, every claim cited to its filing`;
  const b = document.createElement("span");
  b.className = "site-footer__count";
  b.title = `Funding filings MentionFox has tracked since January 1, ${c.year}`;
  b.textContent = `${fmt(c.filings_shown as number)}+ funding filings tracked in ${c.year}`;
  box.replaceChildren(a, b);
}

export function SiteFooter() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    let live = true;
    fetch("/trust-counts.json", { cache: "force-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (live && c) fillCounts(ref.current, c); })
      .catch(() => { /* no counts file: the line shows no counts */ });
    return () => { live = false; };
  }, []);
  // These three attributes mirror the opening tag in src/shared/site-footer.html.
  return (
    <footer
      ref={ref}
      role="contentinfo"
      className="site-footer"
      aria-label="Site footer"
      dangerouslySetInnerHTML={{ __html: INNER }}
    />
  );
}

export default SiteFooter;
